import Phaser from 'phaser';
import type { CombatResult } from '../../game/types.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';

const DIE_SIZE = 60;
const DIE_GAP = 12;
const VS_GAP = 48;
const DIE_CORNER_RADIUS = 8;
const CYCLE_INTERVAL_MS = 70;
const CYCLE_COUNT = 7;
const HOLD_AFTER_SETTLE_MS = 900;

// Pip geometry on a standard 3×3 grid centered in the die. With
// DIE_SIZE=60 the pips comfortably fit inside a ~12px padding from
// each edge.
const PIP_RADIUS = 5;
const PIP_OFFSET = 16; // distance from die center to outer pip column/row

// Hard-coded pip layouts, keyed by face value. Coordinates are
// offsets from the die center.
const FACE_LAYOUT: Readonly<Record<number, readonly [number, number][]>> = {
  1: [[0, 0]],
  2: [
    [-PIP_OFFSET, -PIP_OFFSET],
    [PIP_OFFSET, PIP_OFFSET],
  ],
  3: [
    [-PIP_OFFSET, -PIP_OFFSET],
    [0, 0],
    [PIP_OFFSET, PIP_OFFSET],
  ],
  4: [
    [-PIP_OFFSET, -PIP_OFFSET],
    [PIP_OFFSET, -PIP_OFFSET],
    [-PIP_OFFSET, PIP_OFFSET],
    [PIP_OFFSET, PIP_OFFSET],
  ],
  5: [
    [-PIP_OFFSET, -PIP_OFFSET],
    [PIP_OFFSET, -PIP_OFFSET],
    [0, 0],
    [-PIP_OFFSET, PIP_OFFSET],
    [PIP_OFFSET, PIP_OFFSET],
  ],
  6: [
    [-PIP_OFFSET, -PIP_OFFSET],
    [-PIP_OFFSET, 0],
    [-PIP_OFFSET, PIP_OFFSET],
    [PIP_OFFSET, -PIP_OFFSET],
    [PIP_OFFSET, 0],
    [PIP_OFFSET, PIP_OFFSET],
  ],
};

/**
 * One slot in the tray. Always present (3 attacker + 3 defender) so the
 * row reads as 3v3 even when only some are in play. State transitions:
 *
 *   idle    → grayed out, no pips
 *   preview → cyan-bordered "?" placeholder (this die WILL be rolled)
 *   rolling → white face, pips cycle through faces 1–6
 *   settled → white face, black pips for the rolled value
 *   loss    → red face, black pips still showing the rolled value
 *
 * Pips are drawn into a Graphics object so the dice read as real
 * dice — clean and modern, no PNG assets.
 */
class DieSlot {
  private readonly cx: number;
  private readonly cy: number;
  private readonly face: Phaser.GameObjects.Graphics;
  private readonly questionText: Phaser.GameObjects.Text;
  private readonly pips: Phaser.GameObjects.Graphics;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    this.cx = x;
    this.cy = y;

    // Face is a Graphics object so the corners can be rounded — Phaser
    // Rectangle game objects only do square corners. Drawn fresh on
    // every state change.
    this.face = scene.add.graphics();
    this.drawFace(COLORS.diceIdleFillInt, COLORS.diceIdleStrokeInt, 1);

    // Pips render above the face so they always sit on top regardless
    // of creation order.
    this.pips = scene.add.graphics();
    this.pips.setDepth(1);

    this.questionText = scene.add
      .text(x, y, '', {
        fontFamily: FONT_MONO,
        fontSize: '32px',
        color: COLORS.textMuted,
        fontStyle: '700',
      })
      .setOrigin(0.5)
      .setDepth(1);
  }

  setIdle(): void {
    this.drawFace(COLORS.diceIdleFillInt, COLORS.diceIdleStrokeInt, 1);
    this.pips.clear();
    this.questionText.setText('');
  }

  setPreview(): void {
    this.drawFace(COLORS.diceFillInt, COLORS.accentCyanInt, 2);
    this.pips.clear();
    this.questionText.setText('?');
    this.questionText.setColor(COLORS.diceTextHex);
  }

  setRandomFace(): void {
    const value = 1 + Math.floor(Math.random() * 6);
    this.drawFace(COLORS.diceFillInt, COLORS.accentCyanInt, 2);
    this.renderFace(value, COLORS.dicePipDarkInt);
    this.questionText.setText('');
  }

  setSettled(value: number): void {
    this.drawFace(COLORS.diceFillInt, COLORS.diceStrokeInt, 2);
    this.renderFace(value, COLORS.dicePipDarkInt);
    this.questionText.setText('');
  }

  setLoss(value: number): void {
    this.drawFace(COLORS.diceLossFillInt, COLORS.diceLossFillInt, 2);
    this.renderFace(value, COLORS.dicePipDarkInt);
    this.questionText.setText('');
  }

  /**
   * Redraw the rounded square face with the given fill, stroke color,
   * and stroke width. Anchored on the slot's centerpoint.
   */
  private drawFace(
    fill: number,
    stroke: number,
    strokeWidth: number
  ): void {
    const left = this.cx - DIE_SIZE / 2;
    const top = this.cy - DIE_SIZE / 2;
    this.face.clear();
    this.face.fillStyle(fill, 1);
    this.face.fillRoundedRect(left, top, DIE_SIZE, DIE_SIZE, DIE_CORNER_RADIUS);
    this.face.lineStyle(strokeWidth, stroke, 1);
    this.face.strokeRoundedRect(
      left,
      top,
      DIE_SIZE,
      DIE_SIZE,
      DIE_CORNER_RADIUS
    );
  }

  private renderFace(value: number, pipColor: number): void {
    this.pips.clear();
    this.pips.fillStyle(pipColor, 1);
    const positions = FACE_LAYOUT[value];
    if (!positions) return;
    for (const [dx, dy] of positions) {
      this.pips.fillCircle(this.cx + dx, this.cy + dy, PIP_RADIUS);
    }
  }
}

/**
 * Always-visible 3v3 dice strip. Hosts the same cycle-then-settle
 * animation as the old DiceRollView, but the slots persist between
 * rolls and gray out when no attack is being prepared.
 *
 * Split animation API:
 *   - `startCycle(att, def)` begins the random-face spin without
 *     knowing the actual dice values. Multiplayer calls this on the
 *     attacker's `handleRoll` so the spin starts before the server
 *     round-trip, hiding most of the network latency.
 *   - `playRoll(result, onComplete)` finishes the animation with the
 *     actual values. If `startCycle` was already called, the elapsed
 *     spin time counts against MIN_CYCLE_MS so the settle happens as
 *     soon as the visible cycle has run for "long enough." If not
 *     called, this kicks off the spin itself (single-player flow).
 */
export class DiceTray {
  private readonly scene: Phaser.Scene;
  private readonly attackerSlots: DieSlot[] = [];
  private readonly defenderSlots: DieSlot[] = [];
  private cyclingTimer: Phaser.Time.TimerEvent | undefined;
  private cycleStartedAt: number | null = null;

  constructor(scene: Phaser.Scene, centerX: number, centerY: number) {
    this.scene = scene;

    // Layout (absolute positions — keeps VS exactly on centerX):
    //
    //   [A0][A1][A2]   VS   [D0][D1][D2]
    //
    // Each side block is 3 dice with DIE_GAP between them. Between the
    // two blocks sits a VS_GAP-wide channel with the "VS" label centered
    // in it. Block edges therefore sit at centerX ± VS_GAP/2.
    const sideWidth = 3 * DIE_SIZE + 2 * DIE_GAP;
    const attackerLeftEdge = centerX - VS_GAP / 2 - sideWidth;
    const defenderLeftEdge = centerX + VS_GAP / 2;

    // ATT / DEF side labels — bumped to a chunky 48px so they read
    // as primary side-of-screen anchors rather than tiny captions.
    // Lateral margin outside the dice strip is ~132px on each side at
    // 720 design width, so the larger labels still clear the dice
    // comfortably with LABEL_GAP padding. Tracked-uppercase to match
    // the tactical voice used everywhere else in the action panel.
    const LABEL_GAP = 14;
    scene.add
      .text(attackerLeftEdge - LABEL_GAP, centerY, 'ATT', {
        fontFamily: FONT_HEADER,
        fontSize: '48px',
        color: COLORS.textMuted,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(1, 0.5);

    scene.add
      .text(
        defenderLeftEdge + sideWidth + LABEL_GAP,
        centerY,
        'DEF',
        {
          fontFamily: FONT_HEADER,
          fontSize: '48px',
          color: COLORS.textMuted,
          fontStyle: '700',
        }
      )
      .setLetterSpacing(3)
      .setOrigin(0, 0.5);

    for (let i = 0; i < 3; i++) {
      const x = attackerLeftEdge + DIE_SIZE / 2 + i * (DIE_SIZE + DIE_GAP);
      this.attackerSlots.push(new DieSlot(scene, x, centerY));
    }

    scene.add
      .text(centerX, centerY, 'VS', {
        fontFamily: FONT_HEADER,
        fontSize: '20px',
        color: COLORS.textSecondary,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0.5);

    for (let i = 0; i < 3; i++) {
      const x = defenderLeftEdge + DIE_SIZE / 2 + i * (DIE_SIZE + DIE_GAP);
      this.defenderSlots.push(new DieSlot(scene, x, centerY));
    }

    this.showIdle();
  }

  /** Gray everything out — no attack is being prepared. */
  showIdle(): void {
    this.cancelCycling();
    this.cycleStartedAt = null;
    for (const s of this.attackerSlots) s.setIdle();
    for (const s of this.defenderSlots) s.setIdle();
  }

  /**
   * Start the random-face cycle without an actual result. Multiplayer
   * call site fires this on the attacker's tap so the spin is visible
   * before the server response arrives. A subsequent `playRoll` will
   * pick up from here. Safe to call repeatedly — restarts the cycle.
   */
  startCycle(attackerCount: number, defenderCount: number): void {
    this.cancelCycling();
    this.preview(attackerCount, defenderCount);
    this.cycleStartedAt = this.scene.time.now;
    this.cyclingTimer = this.scene.time.addEvent({
      delay: CYCLE_INTERVAL_MS,
      repeat: -1,
      callback: () => {
        for (let i = 0; i < attackerCount; i++)
          this.attackerSlots[i]?.setRandomFace();
        for (let i = 0; i < defenderCount; i++)
          this.defenderSlots[i]?.setRandomFace();
      },
    });
  }

  /**
   * Highlight `attackerCount` attacker slots and `defenderCount` defender
   * slots in cyan-preview state; the rest stay gray. Used to telegraph
   * "this is what would roll if you hit Roll."
   */
  preview(attackerCount: number, defenderCount: number): void {
    this.cancelCycling();
    for (let i = 0; i < 3; i++) {
      const slot = this.attackerSlots[i]!;
      if (i < attackerCount) slot.setPreview();
      else slot.setIdle();
    }
    for (let i = 0; i < 3; i++) {
      const slot = this.defenderSlots[i]!;
      if (i < defenderCount) slot.setPreview();
      else slot.setIdle();
    }
  }

  /**
   * Finish the dice animation with an actual combat result. If the
   * cycle was already started (multiplayer fast path), the elapsed
   * spin counts against MIN_CYCLE_MS so settle fires the moment the
   * visible spin has run long enough. If not started yet (single-
   * player synchronous path), this kicks off the cycle first.
   *
   * `onComplete` fires after a brief hold so the player reads the
   * result before the panel transitions.
   */
  playRoll(result: CombatResult, onComplete: () => void): void {
    const aCount = result.roll.attackerDice.length;
    const dCount = result.roll.defenderDice.length;

    // Single-player path: cycle wasn't pre-started, start it now.
    if (this.cycleStartedAt === null) {
      this.startCycle(aCount, dCount);
    }

    const startedAt = this.cycleStartedAt ?? this.scene.time.now;
    const MIN_CYCLE_MS = CYCLE_INTERVAL_MS * CYCLE_COUNT;
    const elapsed = this.scene.time.now - startedAt;
    const remainingCycle = Math.max(0, MIN_CYCLE_MS - elapsed);

    this.scene.time.delayedCall(remainingCycle, () => {
      this.cancelCycling();
      this.cycleStartedAt = null;
      result.roll.attackerDice.forEach((v, i) =>
        this.attackerSlots[i]?.setSettled(v)
      );
      result.roll.defenderDice.forEach((v, i) =>
        this.defenderSlots[i]?.setSettled(v)
      );
      this.tintLosers(result);
    });

    this.scene.time.delayedCall(
      remainingCycle + HOLD_AFTER_SETTLE_MS,
      () => {
        onComplete();
      }
    );
  }

  private tintLosers(result: CombatResult): void {
    // Sort copies descending and find which dice lost. Result arrays are
    // in roll order, so we map sorted index back to original via stable
    // sort indices.
    const aSorted = result.roll.attackerDice
      .map((v, i) => ({ v, i }))
      .sort((a, b) => b.v - a.v);
    const dSorted = result.roll.defenderDice
      .map((v, i) => ({ v, i }))
      .sort((a, b) => b.v - a.v);
    const pairs = Math.min(aSorted.length, dSorted.length);

    for (let p = 0; p < pairs; p++) {
      const a = aSorted[p]!;
      const d = dSorted[p]!;
      // Defender wins ties → attacker loses on a <= d.
      if (a.v <= d.v) this.attackerSlots[a.i]?.setLoss(a.v);
      else this.defenderSlots[d.i]?.setLoss(d.v);
    }
  }

  private cancelCycling(): void {
    if (this.cyclingTimer) {
      this.cyclingTimer.remove(false);
      this.cyclingTimer = undefined;
    }
  }
}
